import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridFooterWebComponentSchema, PaginationDetails } from '../types';
import { TemplateState } from '../../../types/common/types';
import { SelectOption } from '../../../ui/gzDropdown/types';
import { GzIconButtonWebComponent } from '../../../ui/gzIconButton';
import { GridSummaryWebComponent } from '../../uiElements/gridSummary';
import { dataGridStrings } from '../../utils/gridStrings';

export class GridFooterView extends BaseWebComponentView {
  private minPageSize: number | null = null;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$backToTop = this.shadow.getElementById('backToTop');
    this.dom.$loadMore = this.shadow.getElementById('loadMore');
    this.dom.$gridDataRefresh = this.shadow.getElementById('gridDataRefresh') as GzIconButtonWebComponent;
    this.dom.$loadItemsCount = this.shadow.getElementById('loadItemsCount');
    this.dom.$paginationFooter = this.shadow.getElementById('paginationFooter');
    this.dom.$gridSummary = this.shadow.getElementById('gridSummary') as GridSummaryWebComponent;

    this.getTranslations();
    this.setTranslatedStrings();

    // getDomElements is called at start, and also when the component finished initializing all its children.
    // Its only in this second step that we have the footer initialized.
    if (this.dom.$paginationFooter.injectDependencies) {
      this.dom.$paginationFooter.injectDependencies(this.dependencies);
      this.dom.$paginationFooter.start().then().catch(console.error);
    }
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$loadMore.innerHTML = this.translations.loadMore;
    this.dom.$backToTop.innerHTML = this.translations.backToTop;
    this.dom.$gridDataRefresh.tooltipText = dataGridStrings().refreshBtnTooltip;
  }

  private buildItemsCountDropdown(pageSize: number) {
    const options: Array<SelectOption> = [
      {
        id: 1,
        title: '250',
        value: '250',
      },
      {
        id: 2,
        title: '150',
        value: '150',
      },
      {
        id: 3,
        title: '100',
        value: '100',
      },
      {
        id: 4,
        title: '50',
        value: '50',
      },
    ];
    let preSelectedOption: SelectOption = options[3];

    for (const option of options) {
      if (option.value === JSON.stringify(pageSize)) {
        preSelectedOption = option;
      }
    }

    this.dom.$loadItemsCount.setAttribute('options', JSON.stringify(options));
    this.dom.$loadItemsCount.setAttribute('selectedOption', JSON.stringify(preSelectedOption));
    this.dom.$loadItemsCount.setAttribute('value', preSelectedOption.value);
  }

  buildSuccessView(response: GridFooterWebComponentSchema) {
    response.hasPagination ? this.buildPaginationFooter(response) : this.buildLoadMoreFooter(response);
  }

  public getPageSize(): number {
    const loadItemsCount = JSON.parse(this.dom.$loadItemsCount.getAttribute('selectedOption')).value;
    return parseInt(loadItemsCount, 10);
  }

  public setDisabledLoadMore(disabled: boolean) {
    disabled
      ? this.setElementClass(this.dom.$loadMore, TemplateState.DISABLED)
      : this.removeElementClass(this.dom.$loadMore, TemplateState.DISABLED);
  }

  private buildLoadMoreFooter(data: GridFooterWebComponentSchema) {
    this.dom.$gridSummary.text = data.gridSummary
      ? data.gridSummary
      : this.dependencies.translateFn('{numTotalItems} items', { numTotalItems: data.numTotalItems });
    this.buildItemsCountDropdown(data.pageSize);
  }

  private buildPaginationFooter(data: GridFooterWebComponentSchema) {
    this.dom.$paginationFooter.propertiesSet = false;
    this.dom.$paginationFooter.pageSize = data.pageSize;
    this.dom.$paginationFooter.currentPage = data.pageNumber;
    this.dom.$paginationFooter.numSelectedItems = data.numSelectedItems;
    this.dom.$paginationFooter.totalNumberOfPages = data.numTotalPages;
    this.dom.$paginationFooter.pageSizeOptions = data.pageSizeOptions;
    this.dom.$gridSummary.text = this.getItemsDetailsTextInPagination({
      currentPage: data.pageNumber,
      pageSize: data.pageSize,
      numTotalItems: data.numTotalItems,
      numSelectedItems: data.numSelectedItems,
    });

    this.dom.$paginationFooter.translations = {
      itemsDetailsText: this.getItemsDetailsTextInPagination({
        currentPage: data.pageNumber,
        pageSize: data.pageSize,
        numTotalItems: data.numTotalItems,
        numSelectedItems: data.numSelectedItems,
      }),
      pageSizeText: this.dependencies.translateFn('Items per page:'),
      pageCountText: this.getPageCountTextInPagination(data.numTotalPages),
    };

    this.dom.$paginationFooter.propertiesSet = true;

    if (this.minPageSize === null) {
      this.minPageSize = Math.min(...data.pageSizeOptions.map(option => parseInt(option.value, 10)));
    }

    this.dom.$paginationFooter.style.display =
      data.numTotalPages === 1 && data.numTotalItems <= this.minPageSize ? 'none' : 'block';
  }

  private getItemsDetailsTextInPagination(paginationDetails: PaginationDetails): string {
    const { currentPage, pageSize, numTotalItems, numSelectedItems } = paginationDetails;

    const leftLimit = numTotalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const rightLimit = Math.min(currentPage * pageSize, numTotalItems);

    if (numSelectedItems > 0) {
      return this.dependencies.translateFn(
        '{firstItemPosition}-{lastItemPosition} of {numTotalItems} items ({numSelectedItems} selected)',
        {
          firstItemPosition: leftLimit,
          lastItemPosition: rightLimit,
          numTotalItems: numTotalItems,
          numSelectedItems: numSelectedItems,
        }
      );
    }

    return this.dependencies.translateFn('{firstItemPosition}-{lastItemPosition} of {numTotalItems} items', {
      firstItemPosition: leftLimit,
      lastItemPosition: rightLimit,
      numTotalItems: numTotalItems,
    });
  }

  private getPageCountTextInPagination(numTotalPages: number): string {
    return this.dependencies.translateFn('of {numPages} pages', { numPages: numTotalPages });
  }
}
